import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import GridOnRounded from '@mui/icons-material/GridOnRounded';
import PrintOutlined from '@mui/icons-material/PrintOutlined';
import { Box, Button, GlobalStyles, Stack, Tooltip, Typography } from '@mui/material';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { useAuth } from '../../../auth/AuthProvider';
import { dateTime } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import { shopHeader } from '../../sales/print/builders';
import { usePrint } from '../../sales/print/usePrint';
import { downloadText, type CsvFile } from '../lib/csv';
import { reportBySlug } from '../lib/reportCatalog';
import { toReportTable, type ReportMeta } from '../lib/reportTable';
import { downloadXlsx } from '../lib/xlsx';
import { ReportDocument } from './ReportDocument';
import { NoAccess } from './ui';
import { useShopLogo } from '../../../lib/images';

/**
 * Print rules for report pages: hide the app chrome and controls, show the print header, let the
 * content use the full page width, and repeat table headers on each page.
 */
export function PrintStyles() {
  return (
    <GlobalStyles
      styles={{
        '.print-only': { display: 'none' },
        '@media print': {
          '@page': { size: 'A4', margin: '14mm 12mm' },
          // Light paper even in dark mode: the dark scheme would paint the page margins dark.
          ':root': { colorScheme: 'light !important' },
          'body, html': { background: '#fff !important' },
          'nav, header, .no-print, .MuiDrawer-root, .notistack-SnackbarContainer': {
            display: 'none !important',
          },
          '.print-only': { display: 'block !important' },
          main: {
            maxWidth: 'none !important',
            padding: '0 !important',
            animation: 'none !important',
          },
          '.MuiCard-root': { boxShadow: 'none !important', breakInside: 'avoid' },
          thead: { display: 'table-header-group' },
          tfoot: { display: 'table-row-group' },
          'tr, td, th': { breakInside: 'avoid' },
          '*': { WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' },
        },
      }}
    />
  );
}

/** Back link used by every report and detail page. */
export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Button
      component={RouterLink}
      to={to}
      size="small"
      startIcon={<ArrowBackRounded />}
      className="no-print"
      sx={{ color: 'text.secondary', ml: -1, mb: 1 }}
    >
      {label}
    </Button>
  );
}

type Output = 'print' | 'xlsx' | 'csv';

/**
 * The page around one report: back link, title, filter bar, and the outputs — Print / PDF (an A4
 * document with the full table, landscape when wide), Excel (formatted .xlsx) and CSV. All three
 * come from the report's CSV export, so they need `reports.export`; without it Print falls back to
 * printing the screen. Shows a no-access state when the user lacks the report's permissions.
 */
export function ReportFrame({
  slug,
  period,
  controls,
  onExport,
  children,
}: {
  slug: string;
  /** Human description of the filters for the print header, e.g. "1 – 20 Oct 2026". */
  period?: string;
  controls?: ReactNode;
  /** Fetches the report's CSV export (`fetchCsv`); omitted when the report has no export. */
  onExport?: () => Promise<CsvFile>;
  children: ReactNode;
}) {
  const info = reportBySlug(slug);
  const { can } = useAuth();
  const { settings, format } = useShop();
  const logo = useShopLogo();
  const notify = useNotify();
  const [busy, setBusy] = useState<Output | null>(null);
  const { print, element: printElement } = usePrint();
  const allowed = can(...info.perms);
  const canExport = can('reports.export');
  const tableOutputs = Boolean(onExport) && canExport;

  const run = (output: Output) => {
    if (!onExport) return;
    setBusy(output);
    onExport()
      .then(async (file) => {
        if (output === 'csv') {
          downloadText(file.text, file.filename);
          notify.success('CSV downloaded');
          return;
        }
        const table = toReportTable(file.text, file.filename.replace(/\.csv$/i, ''));
        const meta: ReportMeta = {
          shopName: settings?.name ?? 'Shop',
          title: info.title,
          ...(period ? { period } : {}),
          generatedAt: dateTime(new Date().toISOString(), format),
        };
        if (output === 'xlsx') {
          await downloadXlsx(table, meta, settings?.themeColor);
          notify.success('Excel file downloaded');
        } else {
          print(
            table.columns.length > 6 ? 'a4-landscape' : 'a4',
            <ReportDocument
              shop={shopHeader(settings, undefined, logo)}
              meta={meta}
              table={table}
              format={format}
            />,
          );
        }
      })
      .catch((e: unknown) => notify.error(e))
      .finally(() => setBusy(null));
  };

  return (
    <>
      <PrintStyles />
      {printElement}
      <BackLink to="/reports" label="All reports" />
      <Box className="print-only" sx={{ mb: 2 }}>
        <Typography variant="overline" color="text.secondary">
          {settings?.name ?? ''}
        </Typography>
      </Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ alignItems: { md: 'flex-end' }, justifyContent: 'space-between', mb: 2.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3">{info.title}</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {info.description}
          </Typography>
          {period && (
            <Typography className="print-only" variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
              {period} · printed {dateTime(new Date().toISOString(), format)}
            </Typography>
          )}
        </Box>
        {allowed && (
          <Stack
            direction="row"
            className="no-print"
            sx={{ flexShrink: 0, gap: 1, flexWrap: 'wrap' }}
          >
            <Tooltip
              describeChild
              title="An A4 document with every row — choose “Save as PDF” to get a file"
            >
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PrintOutlined />}
                loading={busy === 'print'}
                onClick={() => (tableOutputs ? run('print') : window.print())}
                sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}
              >
                Print / PDF
              </Button>
            </Tooltip>
            {onExport && (
              <>
                <Tooltip
                  describeChild
                  title={canExport ? 'Plain comma-separated file' : 'Needs export permission'}
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="inherit"
                      startIcon={<FileDownloadOutlined />}
                      disabled={!canExport || busy !== null}
                      loading={busy === 'csv'}
                      onClick={() => run('csv')}
                      sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}
                    >
                      CSV
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  describeChild
                  title={canExport ? 'Formatted Excel workbook (.xlsx)' : 'Needs export permission'}
                >
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<GridOnRounded />}
                      disabled={!canExport || busy !== null}
                      loading={busy === 'xlsx'}
                      onClick={() => run('xlsx')}
                    >
                      Excel
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Stack>
        )}
      </Stack>
      {!allowed ? (
        <NoAccess what={`the ${info.title.toLowerCase()} report`} />
      ) : (
        <>
          {controls && (
            <Stack
              direction="row"
              className="no-print"
              sx={{ gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2.5 }}
            >
              {controls}
            </Stack>
          )}
          {children}
        </>
      )}
    </>
  );
}
